'use client';

import type { Hit } from 'meilisearch';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import type { FocusEventHandler } from 'react';
import { useCallback, useRef } from 'react';
import type { SelectInstance } from 'react-select';
import { useKey } from 'react-use';

import { meilisearch } from '@/lib/search/meilisearch';
import type { NewsroomSlug } from '@/types';

import { IndicatorsContainer } from './IndicatorsContainer/IndicatorsContainer';
import type { IndexedDocument } from './types';

// Dynamically importing the whole Search component doesn't reduce the bundle size at all.
// It seems it only works for imports in client components.
const Async = dynamic(() => import('react-select/async'));

const SearchControl = dynamic(() => import('./SearchControl/SearchControl'));
const SearchMenu = dynamic(() => import('./SearchMenu/SearchMenu'));
const SearchPlaceholder = dynamic(() => import('./SearchPlaceholder/SearchPlaceholder'));
const SearchResult = dynamic(() => import('./SearchResult/SearchResult'));

interface Props {
    className?: string;
    placeholder?: string;
    searchIndex: string;
    newsroom: NewsroomSlug;
    view?: 'compact' | 'big';
    theme?: 'dark' | 'light';
    onFocus?: FocusEventHandler;
}

export function Search({
    className,
    placeholder,
    searchIndex,
    newsroom,
    view,
    theme,
    onFocus,
}: Props) {
    const router = useRouter();
    const selectRef = useRef<SelectInstance<Hit<IndexedDocument>>>(null);

    const onSearchResultSelect = useCallback(
        (hit: Hit | null) => {
            if (hit) {
                router.push(hit.path);
            }
        },
        [router],
    );

    const fetchSearchResults = useCallback(
        async (query: string) => {
            const { hits } = await meilisearch.index(searchIndex).search<IndexedDocument>(query, {
                filter: [`tags = ${newsroom}`],
                attributesToHighlight: ['*'],
            });

            return hits.filter(Boolean).map((hit) => ({
                ...hit,
                value: hit.id,
            }));
        },
        [searchIndex, newsroom],
    );

    useKey('/', () => {
        const select = selectRef.current;

        if (!select) {
            return;
        }

        // timeout is needed to prevent input from receiving "/"
        setTimeout(() => select.focus(), 0);
    });

    return (
        // @ts-ignore
        <Async
            className={className}
            ref={selectRef}
            cacheOptions
            onFocus={onFocus}
            components={{
                Control: SearchControl,
                IndicatorsContainer,
                Option: SearchResult,
                Placeholder: SearchPlaceholder,
                Menu: SearchMenu,
            }}
            loadOptions={fetchSearchResults}
            placeholder={placeholder}
            onChange={onSearchResultSelect}
            view={view}
            colorTheme={theme}
            noOptionsMessage={() => 'Nothing found with that search term'}
            blurInputOnSelect
        />
    );
}
